import { BackTop, message, Spin } from "antd";
import React, { useEffect, useRef, useCallback } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { updateUser } from "redux/actions/userActions";
import { apiGetHomepageData, apiGetToolbarItems } from "services/dashboardService";
import { trackCategoryClick } from "services/statsService";
import GridSection from "./GridSection/GridSection";
import SingleHomepage from "./SingleHomepage/SingleHomepage";
import Layout from "./Layout";
import { UpdateAppInfo } from "redux/actions/appActions";
import { getOrganizationFromDomain } from "utils/paymentConfig";
import "pages/InfoPage/InfoPage.css";

const shouldLogPerf = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("beclutch_perf_debug") === "1";
  } catch {
    return false;
  }
};

const logPerf = (label, startedAt) => {
  if (!shouldLogPerf()) return;
  const elapsed = Math.round((performance.now() - startedAt) * 10) / 10;
  console.info(`[beclutch-perf] ${label}: ${elapsed}ms`);
};

const HomePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isBusy, setIsBusy] = useState(false);
  const [appInfo, setAppInfo] = useState({
    homepage_categories: [],
    registrations_by_category: {},
    site_config: {}
  });
  const [curDomain, setCurDomain] = useState("");
  const [fixedHtml, setFixedHtml] = useState(null);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [toolbarItems, setToolbarItems] = useState([]);
  const [toolbarEnabled, setToolbarEnabled] = useState(false);
  const [selectedToolbarPage, setSelectedToolbarPage] = useState(null);
  const [toolbarPageHtml, setToolbarPageHtml] = useState(null);
  const [loadingToolbarPage, setLoadingToolbarPage] = useState(false);
  const iframeRef = useRef(null);
  const toolbarIframeRef = useRef(null);

  useEffect(() => {
    let tmpDomain = window.location.hostname;
    // Handle development environment
    if (tmpDomain.trim() === "localhost" || tmpDomain.trim() === "192.168.100.81") {
      // Default to demo for development
      tmpDomain = "demo.beclutch.club";
    }

    // Validate domain
    const org = getOrganizationFromDomain(tmpDomain);
    if (!org) {
      console.error(`Invalid domain: ${tmpDomain}`);
      message.error("Invalid domain configuration");
      return;
    }

    setCurDomain(tmpDomain);
    // Clear any existing user data
    localStorage.removeItem("camper_user_info");
    localStorage.removeItem("camper_step");
    localStorage.removeItem("camper_registration_type");
    dispatch(updateUser({ camp_users: [], first_name: "", last_name: "", email: "", cell_phone: "", token: undefined }));
    dispatch(UpdateAppInfo({ appInfo: undefined }));
    getData(tmpDomain);
  }, []);

  // Fetch toolbar items when toolbar is enabled
  useEffect(() => {
    const siteConfig = appInfo?.site_config;
    if (siteConfig?.toolbar_enabled == 1) {
      setToolbarEnabled(true);
      if (curDomain) {
        const startedAt = performance.now();
        apiGetToolbarItems(curDomain)
          .then((res) => {
            if (res.result && res.data?.toolbar_items) {
              setToolbarItems(res.data.toolbar_items);
            }
            logPerf("toolbar_items_fetch", startedAt);
          })
          .catch((err) => {
            console.error("Failed to fetch toolbar items:", err);
            logPerf("toolbar_items_fetch_failed", startedAt);
          });
      }
    } else {
      setToolbarEnabled(false);
      setToolbarItems([]);
    }
  }, [appInfo?.site_config, curDomain]);

  // Load toolbar page HTML when a toolbar item is selected
  useEffect(() => {
    if (!selectedToolbarPage) {
      setToolbarPageHtml(null);
      return;
    }
    setLoadingToolbarPage(true);
    const fileUrl = `/pages/${selectedToolbarPage}?t=${Date.now()}`;
    fetch(fileUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => {
        const rewritten = html
          .replace(/src="(?!http|\/|#)(.*?)"/g, 'src="/pages/$1"')
          .replace(/href="(?!http|\/|#|mailto)(.*?)"/g, 'href="/pages/$1"');
        setToolbarPageHtml(rewritten);
        setLoadingToolbarPage(false);
      })
      .catch(err => {
        console.error("[HomePage] Failed to load toolbar page HTML:", err);
        setToolbarPageHtml(null);
        setLoadingToolbarPage(false);
      });
  }, [selectedToolbarPage]);

  // Load info page HTML when site_config indicates info_page mode
  useEffect(() => {
    const siteConfig = appInfo?.site_config;
    if ((siteConfig?.info_page === "1" || siteConfig?.info_page === 1) && siteConfig?.info_page_file) {
      setLoadingHtml(true);
      const fileUrl = `/pages/${siteConfig.info_page_file}?t=${Date.now()}`;
      fetch(fileUrl)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then(html => {
          const rewritten = html
            .replace(/src="(?!http|\/|#)(.*?)"/g, 'src="/pages/$1"')
            .replace(/href="(?!http|\/|#|mailto)(.*?)"/g, 'href="/pages/$1"');
          setFixedHtml(rewritten);
          setLoadingHtml(false);
        })
        .catch(err => {
          console.error("[HomePage] Failed to load info page HTML:", err);
          setFixedHtml(null);
          setLoadingHtml(false);
        });
    }
  }, [appInfo?.site_config]);

  const resizeIframe = useCallback((ref) => {
    const iframe = ref?.current || iframeRef.current;
    if (iframe && iframe.contentDocument) {
      const body = iframe.contentDocument.body;
      if (body) {
        iframe.style.height = body.scrollHeight + "px";
      }
    }
  }, []);

  const getData = (tmpDomain) => {
    const startedAt = performance.now();
    setIsBusy(true);
    apiGetHomepageData(tmpDomain)
      .then((res) => {
        setIsBusy(false);
        if (res.result) {
          dispatch(UpdateAppInfo({ appInfo: res.data }));
          setAppInfo(res.data);
          logPerf("homepage_data_fetch", startedAt);
        } else {
          message.error(res.msg || "Failed to load homepage data");
          logPerf("homepage_data_fetch_failed", startedAt);
        }
      })
      .catch((err) => {
        setIsBusy(false);
        console.error("Homepage data error:", err);
        message.error("Something went wrong loading homepage");
        logPerf("homepage_data_fetch_failed", startedAt);
      });
  };

  const handleCategoryClick = (categoryId) => {
    trackCategoryClick(categoryId);
    const category = appInfo?.homepage_categories?.find(
      cat => String(cat.id) === String(categoryId)
    );
    if (category?.info_page_enabled === "1" || category?.info_page_enabled === 1) {
      history.push(`/info/${categoryId}`);
    } else {
      history.push(`/category/${categoryId}`);
    }
  };

  const isInfoPageOnly = appInfo?.site_config?.info_page === "1" || appInfo?.site_config?.info_page === 1;
  const isSingleHomepage = appInfo?.site_config?.homepage_type === "single";

  const mainContent = isSingleHomepage ? (
    <SingleHomepage
      siteConfig={appInfo?.site_config}
      categories={appInfo?.homepage_categories || []}
      registrationsByCategory={appInfo?.registrations_by_category || {}}
    />
  ) : isInfoPageOnly ? (
    <div className="info-page">
      <div className="info-content-container">
        <div className="info-fixed-content">
          {loadingHtml && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
            </div>
          )}
          {fixedHtml && (
            <iframe
              ref={iframeRef}
              srcDoc={fixedHtml}
              title="Info Page"
              className="info-iframe-auto"
              frameBorder="0"
              scrolling="no"
              onLoad={() => resizeIframe(iframeRef)}
            />
          )}
        </div>
      </div>
    </div>
  ) : (
    <GridSection
      categories={appInfo?.homepage_categories || []}
      onCategoryClick={handleCategoryClick}
    />
  );

  return (
    <Spin spinning={isBusy}>
      <Layout appInfo={appInfo} className={isSingleHomepage ? "dashboard-main-page--light" : ""}>
        {toolbarEnabled ? (
          <div className="homepage-with-toolbar">
            <div className="homepage-toolbar">
              <div
                className={`toolbar-item ${!selectedToolbarPage ? "toolbar-item-active" : ""}`}
                onClick={() => {
                  setSelectedToolbarPage(null);
                  history.push("/");
                }}
                title="Home"
              >
                <img
                  src="/assets/images/Home.png"
                  alt="Home"
                  className="toolbar-item-icon"
                />
                <span className="toolbar-item-label">Home</span>
              </div>
              {toolbarItems.map((item, index) => (
                <div
                  key={index}
                  className={`toolbar-item ${selectedToolbarPage === item.page_file ? "toolbar-item-active" : ""}`}
                  onClick={() => setSelectedToolbarPage(
                    selectedToolbarPage === item.page_file ? null : item.page_file
                  )}
                  title={item.name}
                >
                  {item.image_url && (
                    <img
                      src={item.image_url.replace(/^http:\/\/(44\.195\.118\.100|mobile\.beclutch\.club)/, 'https://mobile.beclutch.club')}
                      alt={item.name}
                      className="toolbar-item-icon"
                    />
                  )}
                  <span className="toolbar-item-label">{item.name}</span>
                </div>
              ))}
            </div>
            <div className="homepage-main-content">
              {selectedToolbarPage ? (
                <div className="toolbar-page-content">
                  {loadingToolbarPage && (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <Spin size="large" />
                    </div>
                  )}
                  {toolbarPageHtml && (
                    <iframe
                      ref={toolbarIframeRef}
                      srcDoc={toolbarPageHtml}
                      title="Toolbar Page"
                      className="info-iframe-auto"
                      frameBorder="0"
                      scrolling="no"
                      onLoad={() => resizeIframe(toolbarIframeRef)}
                    />
                  )}
                </div>
              ) : (
                mainContent
              )}
            </div>
          </div>
        ) : (
          mainContent
        )}
        <BackTop />
      </Layout>
    </Spin>
  );
};

export default HomePage;
